import _ from "lodash";
import Reflux from "reflux";
import AppConstants from "../constants/AppConstants";
import { getSiteData, getSiteId, getSiteName } from "../hooks/query/useSite";
import ExportActions from "../actions/ExportActions";
import UIActions from "../actions/UIActions";
import SiteService from "../service/SiteService.mjs";
import APIError from "../service/APIError";
import ErrorUtils from "../utils/ErrorUtils";
import { queryClient } from "../utils/QueryUtils";
import { EXPORTED_DESIGNS } from "../constants/QueryKeys";
import {
  EXPORT_VIEW_COMPLETE,
  EXPORT_VIEW_ERROR,
  EXPORT_VIEW_INITIAL,
  EXPORT_VIEW_LOADING,
  EXPORT_VIEW_PROCESSING,
} from "@/constants/ExportViewConstants.mjs";
import { CANVAS, PAGE, SITE } from "@/constants/DesignExportDescriptors.mjs";
import {
  DESIGN_TYPE_PAGE,
  DESIGN_TYPE_BLOCK,
  DESIGN_TYPE_WP_BLOCK,
  DESIGN_TYPE_WP_PAGE,
} from "@/constants/DesignExportTypes.mjs";

/**
 * @typedef {import("@/constants/ExportViewConstants.mjs").EXPORT_VIEW_COMPLETE} ExportViewComplete
 * @typedef {import("@/constants/ExportViewConstants.mjs").EXPORT_VIEW_ERROR} ExportViewError
 * @typedef {import("@/constants/ExportViewConstants.mjs").EXPORT_VIEW_INITIAL} ExportViewInitial
 * @typedef {import("@/constants/ExportViewConstants.mjs").EXPORT_VIEW_LOADING} ExportViewLoading
 * @typedef {import("@/constants/ExportViewConstants.mjs").EXPORT_VIEW_PROCESSING} ExportViewProcessing
 * @typedef {import("@/constants/ExportViewConstants.mjs").EXPORT_VIEW_SAVING} ExportViewSaving
 *
 * @typedef {import("@/constants/DesignExportTypes.mjs").DESIGN_TYPE_SITE} ExportTypeSite
 * @typedef {import("@/constants/DesignExportTypes.mjs").DESIGN_TYPE_PAGE} ExportTypePage
 * @typedef {import("@/constants/DesignExportTypes.mjs").DESIGN_TYPE_BLOCK} ExportTypeCanvas
 * @typedef {import("@/constants/DesignExportTypes.mjs").DESIGN_TYPE_SITE_BLOCK} ExportTypeSiteCanvas
 * @typedef {import("@/constants/DesignExportTypes.mjs").DESIGN_TYPE_WP_PAGE} ExportTypeBlogPage
 * @typedef {import("@/constants/DesignExportTypes.mjs").DESIGN_TYPE_WP_BLOCK} ExportTypeBlogCanvas
 *
 * @typedef {import("@/service/SiteService.mjs").DesignExportRecord} DesignExportRecord
 *
 * @typedef {Object} CanvasExport
 * @prop {string=} blockId
 * @prop {string=} blockName
 *
 *
 * @typedef {Object} PageExport
 * @prop {string=} pageId
 * @prop {string=} pageName
 *
 * @typedef {Object} SiteExport
 * @prop {string | null} desc
 * @prop {ExportTypeBlogCanvas | ExportTypeBlogPage | ExportTypeCanvas | ExportTypePage | ExportTypeSite | ExportTypeSiteCanvas } designType
 * @prop {string | null } exportId
 * @prop {string=} exportKey
 * @prop {string=} name - Name of the export, could be site, page, or block name.
 * @prop {string} siteId
 * @prop {string=} siteName
 * @prop {typeof CANVAS | typeof PAGE | typeof SITE } typeDesc - Level of export within a site
 *
 * @typedef {CanvasExport & PageExport & SiteExport} DesignExport
 *
 * @typedef {Object} Process
 * @prop {ExportViewComplete | ExportViewError | ExportViewInitial | ExportViewLoading | ExportViewProcessing | ExportViewSaving} view
 * @prop {string | false} error
 *
 * @typedef {Object} DesignExportState
 * @prop {DesignExport} design
 * @prop {DesignExportRecord[]=} designExports
 * @prop {Process} process
 **/

var DesignExportStore = Reflux.createStore({
  storeName: "DesignExportStore",
  listenables: [ExportActions],

  init() {
    /** @type {DesignExportState} */
    this.settings = {};
  },

  /**
   * Prepares load data for export and starts it
   * @param {Object} data
   * @param {DesignExport["designType"]} data.designType
   */
  onExportDesignBegin(data) {
    const siteId = getSiteId();
    const siteName = getSiteName();

    /** @type {DesignExport} */
    const design = {
      designType: data.designType,
      typeDesc: SITE,
      siteId: siteId,
      siteName: siteName,
      name: siteName,
      desc: null,
      exportId: null,
    };

    switch (data.designType) {
      case DESIGN_TYPE_PAGE:
      case DESIGN_TYPE_WP_PAGE:
        design.pageId = _.get(data, "pageId");
        design.pageName = _.get(data, "pageName");
        design.typeDesc = PAGE;
        design.name = design.pageName;

        break;
      case DESIGN_TYPE_BLOCK:
      case DESIGN_TYPE_WP_BLOCK:
        design.blockId = _.get(data, "blockId");
        design.blockName = _.get(data, "blockName");
        design.pageName = _.get(data, "pageName");
        design.typeDesc = "Canvas";
        design.name = design.blockName;

        break;
    }

    this._loadDesignExportInfo(design);

    const initState = {
      process: {
        view: EXPORT_VIEW_LOADING,
        error: false,
      },
      design: design,
      designExports: [],
      origName: design.name,
    };

    this.settings = initState;

    UIActions.modalOpen(AppConstants.MODAL_DESIGN_EXPORT);
  },

  /**
   *
   * @param {DesignExport} design
   */
  onExportDesign(design) {
    this.settings.design = design;

    this.settings.process.view = EXPORT_VIEW_PROCESSING;
    const renderer = getSiteData()?.renderer ?? 1;

    // todo: what's the purpose of reassigning?
    var design = this.settings.design;
    var designInfo = _.pick(design, [
      "exportId",
      "designType",
      "name",
      "desc",
      "pageId",
      "blockId",
    ]);

    if (_.isEmpty(designInfo.desc)) {
      delete designInfo["desc"];
    }

    if (!_.isNumber(designInfo.exportId)) {
      delete designInfo["exportId"];
    }

    var siteId = design.siteId;

    const exportEndpoint = (
      renderer === AppConstants.RENDERER_V2
        ? SiteService.exportDesignV2
        : SiteService.exportDesign
    ).bind(SiteService);

    exportEndpoint(siteId, designInfo)
      .then(this.onExportComplete.bind(this))
      .catch(this._onHandleServiceError.bind(this));

    this.trigger(this.settings);
  },

  onExportComplete(data) {
    const { pub_key, has_preview_imgs } = data.publication;
    const { designExport } = data;

    // If there is react query data for exported designs already, remove it so it gets fresh data on next view
    queryClient.removeQueries({ queryKey: [EXPORTED_DESIGNS] });

    this.settings.design.exportKey = designExport.key;
    this.settings.process.view = EXPORT_VIEW_COMPLETE;

    this.trigger(this.settings);
  },

  /**
   * Loads relevant design data for export
   * @param {DesignExport} design
   */
  _loadDesignExportInfo(design) {
    // todo: pick probably isn't necessary anymore
    var query = _.pick(design, ["designType", "pageId", "blockId"]);
    SiteService.getDesignExports(design.siteId, query)
      .then(this._onExportInfoComplete.bind(this))
      .catch(this._onHandleServiceError.bind(this));
  },

  /**
   *
   * @param {Awaited<ReturnType<SiteService["getDesignExports"]>>} data
   */
  _onExportInfoComplete(data) {
    var designExports = data.designExports;
    this.settings.designExports = designExports;

    if (_.size(designExports) > 0) {
      var newestExport = designExports[0];
      this.settings.design.exportId = newestExport.export_id;
      this.settings.design.name = newestExport.name;
      this.settings.design.desc = newestExport.desc;
    }

    this.settings.process.view = EXPORT_VIEW_INITIAL;
    this.trigger(this.settings);
  },

  _onHandleServiceError(err) {
    if (APIError.handle(err)) {
      return;
    }

    var error = ErrorUtils.getFormattedError(err);

    this.settings.process.error = error;
    this.settings.process.view = EXPORT_VIEW_ERROR;

    this.trigger(this.settings);
  },

  /** @returns {DesignExportState} */
  getInitialState() {
    return this.settings;
  },
});

export default DesignExportStore;
